import { useForm } from 'react-hook-form';
import { useYupValidationResolver } from './useYupValidationResolver';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import {
    SmsSchema,
    WhatsAppSchema,
    WhatsAppBusinessSchema,
    LiveChatSchema,
    FacebookSchema,
    EmailSchema,
    HostedEmailSchema,
} from '@features/ChannelsForms/schemas';
import { CHANNEL, DEFAULT_RETURN_PATH_SUBDOMAIN, HOSTED_EMAIL_FORM } from '@constants/channels';
import { DEFAULT_WIDGET_ALIGNMENT } from '@constants/widget';
import { LOCATIONS_MODE } from '@constants/liveChat';

const SchemaFactory = {
    [CHANNEL.SMS]: SmsSchema,
    [CHANNEL.WHATSAPP]: WhatsAppSchema,
    [CHANNEL.LIVE_CHAT]: LiveChatSchema,
    [CHANNEL.FACEBOOK]: FacebookSchema,
    [CHANNEL.INSTAGRAM]: FacebookSchema,
    [CHANNEL.EMAIL]: EmailSchema,
    [HOSTED_EMAIL_FORM]: HostedEmailSchema,
    [CHANNEL.WHATSAPP_BUSINESS]: WhatsAppBusinessSchema,
};

const DefaultValuesFactory = {
    [CHANNEL.SMS]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        phoneNumber: '',
        senderId: '',
        provider: 0,
        integrationId: '',
        accountId: '',
        apiKey: '',
        token: '',
        login: '',
        password: '',
    },
    [CHANNEL.WHATSAPP]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        newWhatsAppAccount: false,
    },
    [CHANNEL.WHATSAPP_BUSINESS]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        phoneNumber: '',
        phoneNumberId: '',
        phoneNumberName: '',
        whatsAppBusinessId: '',
    },
    [CHANNEL.LIVE_CHAT]: {
        name: '',
        teamId: '',
        brandId: '',
        labels: [],
        assignLocation: false,
        locationsMode: LOCATIONS_MODE.ALL,
        locations: [],
        notifyAboutMissingConversations: false,
        followUpChannelId: '',
        texts: [],
        buttonCount: 0,
        welcomePopupEnabled: true,
        workingHoursEnabled: false,
        workingHours: { timeZone: '', days: [] },
        emailEnabled: false,
        emailRequired: false,
        phoneEnabled: true,
        phoneRequired: true,
        nameEnabled: true,
        nameRequired: true,
        alignment: DEFAULT_WIDGET_ALIGNMENT,
        offsetX: '',
        offsetY: '',
        allowedSites: [],
    },
    [CHANNEL.FACEBOOK]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        pageKey: '',
        pageId: '',
        integrationId: null,
        id: undefined,
        saveInstagramReplies: true,
    },
    [CHANNEL.INSTAGRAM]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        pageKey: '',
        pageId: '',
        integrationId: null,
        id: undefined,
        saveInstagramReplies: true,
    },
    [CHANNEL.EMAIL]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        provider: 0,
        smtpHost: '',
        smtpPort: '',
        username: '',
        isSSL: true,
        email: '',
        password: '',
        enableIncomingEmail: false,
        imapHost: '',
        imapPort: '',
        imapUsername: '',
        imapPassword: '',
        signature: '',
    },
    [HOSTED_EMAIL_FORM]: {
        name: '',
        teamId: '',
        channelLinkEnabled: false,
        locationType: LOCATION_TYPE.LOCATION,
        locationId: '',
        brandId: '',
        email: '',
        returnPathSubdomain: DEFAULT_RETURN_PATH_SUBDOMAIN,
    },
};

export const useEditChannelsForm = type => {
    const resolver = useYupValidationResolver(SchemaFactory[type]);

    const {
        getValues,
        setValue,
        control,
        reset,
        watch,
        trigger,
        formState: { isValid, isDirty },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: DefaultValuesFactory[type],
    });

    return {
        getValues,
        setValue,
        control,
        reset,
        watch,
        trigger,
        isValid,
        isDirty,
    };
};
